import { LocationSearch } from "@/components/location/LocationSearch.tsx";
import {
  matchedCoordinates,
  SAVED_ADDRESS_LABELS,
  type SavedAddressCoordinates,
  type SavedAddressType,
  useSavedAddresses,
} from "@/components/location/useSavedAddresses.ts";
import { Skeleton } from "@/components/ui/skeleton.tsx";
import type { LocationSuggestion } from "@/lib/places.ts";
import { Building2, Home } from "lucide-react";
import { useState } from "react";

/**
 * The home-and-work address chooser, sized for the omnibox card.
 *
 * Both fields are visible at once rather than stepped through one at a time:
 * there are exactly two, and a user who has just been asked where to plan around
 * can see the whole question in the space one field would take.
 *
 * Each field commits on its own and with no Save button: picking off the Places
 * typeahead saves straight away, and typed text saves when the field is left.
 * So a user who fills in home and dismisses the card keeps home. Clearing an
 * address is left to Settings and the canvas card — this one is for answering
 * the question that was asked, and a trash can wedged into a 50px field beside
 * the autocomplete is a mis-click waiting to happen.
 */
export const LocationsOmniPickerBody = () => {
  const { addresses, isLoading, isSaving, save } = useSavedAddresses();

  return (
    // One grouped table rather than two cards, matching the settings screen and
    // the timezone rows: the group owns the edge, the fields are flush inside
    // it, and a hairline is all that separates them. Nothing clips it, or the
    // address typeahead hanging below the last field would be cut off.
    <div className="flex flex-col rounded-md border border-input">
      <AddressField
        type="home"
        icon={<Home />}
        saved={addresses.home}
        isLoading={isLoading}
        isSaving={isSaving("home")}
        onSave={(address, coordinates) => save("home", address, coordinates)}
      />
      <div className="border-t-[0.5px] border-border" />
      <AddressField
        type="work"
        icon={<Building2 />}
        saved={addresses.work}
        isLoading={isLoading}
        isSaving={isSaving("work")}
        onSave={(address, coordinates) => save("work", address, coordinates)}
      />
    </div>
  );
};

/**
 * One always-editable address field.
 *
 * The typed value is held as a draft that is *absent* until the user types, so
 * the field reads through to the saved address the rest of the time — a
 * background refetch (or the other field's save invalidating the user context)
 * shows through instead of being pinned to a stale copy, and no effect has to
 * guess whether it is safe to overwrite what the user is typing.
 */
const AddressField = ({
  type,
  icon,
  saved,
  isLoading,
  isSaving,
  onSave,
}: {
  type: SavedAddressType;
  icon: React.ReactNode;
  saved: string;
  isLoading: boolean;
  isSaving: boolean;
  onSave: (address: string, coordinates?: SavedAddressCoordinates) => Promise<boolean>;
}) => {
  const [draft, setDraft] = useState<string | null>(null);
  const [pending, setPending] = useState<LocationSuggestion | null>(null);

  const label = SAVED_ADDRESS_LABELS[type];
  const value = draft ?? saved;
  const trimmed = value.trim();
  // Nothing to write when the field still says what's saved, and an emptied
  // field is not a delete — clearing an address is Settings' job.
  const hasEdit = trimmed.length > 0 && trimmed !== saved;

  const commit = async (address: string, suggestion: LocationSuggestion | null) => {
    if (await onSave(address, matchedCoordinates(suggestion, address))) {
      // Back to reading through to the saved value, which is now this one.
      setDraft(null);
      setPending(null);
    }
  };

  /**
   * A pick off the autocomplete saves straight away, with the coordinates that
   * came with it, so the common path is one click and no round trip through the
   * blur below.
   */
  const handleSelect = (suggestion: LocationSuggestion) => {
    const address = suggestion.address || suggestion.name;
    setDraft(address);
    setPending(suggestion);
    void commit(address, suggestion);
  };

  return (
    <div className="flex flex-col">
      {/* While loading, the field frame stands itself in: icon and label are
          static, so they render for real and only the input line is
          skeletoned — an empty input would read as "not set", a different
          answer from "not loaded yet". The frame holds the field's height so
          the card doesn't grow under the cursor as the addresses land. */}
      {isLoading ? (
        <div className="flex min-h-[56px] items-center gap-2.5 p-2.5">
          <div className="flex h-8 items-center justify-center">
            <span className="text-muted-foreground [&>svg]:size-4">{icon}</span>
          </div>
          <div className="flex flex-1 flex-col gap-1">
            <div className="text-xs text-muted-foreground">{label}</div>
            <Skeleton className="h-4 w-40" />
          </div>
        </div>
      ) : (
        /* The three ways a typed address lands, wired here rather than inside
           `LocationSearch` so its other callers keep their own behaviour.

           `onBlur` is the one that matters: leaving the field saves it, so there
           is no Save button anywhere on this card. It commits on the focusout
           itself rather than behind a short timer, because the card can be gone
           a moment later — clicking "Done" claims the picker and re-renders the
           biscuit — and a timer that hasn't fired yet takes the address with it.

           `onMouseDown` is what makes that safe next to the typeahead. Clicking
           a suggestion blurs the input before the suggestion's own click lands,
           which would save the half-typed text a beat before `handleSelect`
           saves the real address. Suppressing the default over any button in
           here — the suggestions and "Current location" alike — keeps focus in
           the input, so the pick is the only write. It is `LocationSearch`'s own
           trick for its clear button, applied to the rows it can't reach.

           `onKeyDown` just spares the keyboard user the trip out of the field. */
        <div
          onBlur={() => {
            if (!hasEdit || isSaving) return;
            void commit(trimmed, pending);
          }}
          onMouseDown={(e) => {
            if (e.target instanceof HTMLElement && e.target.closest("button")) e.preventDefault();
          }}
          onKeyDown={(e) => {
            // defaultPrevented is the dropdown claiming the Enter for a
            // highlighted row; committing here too would save the half-typed
            // text over the pick.
            if (e.key !== "Enter" || e.defaultPrevented || !hasEdit || isSaving) return;
            e.preventDefault();
            void commit(trimmed, pending);
          }}
        >
          {/* The field carries its own identity, in the settings rows'
              grammar: icon inside on the left, the label stacked over the
              input line. */}
          <LocationSearch
            value={value}
            onChange={setDraft}
            onLocationSelect={handleSelect}
            placeholder={`Search for a ${label.toLowerCase()} address`}
            disabled={isSaving}
            showCurrentLocation
            showDropdown
            icon={<span className="text-muted-foreground [&>svg]:size-4">{icon}</span>}
            label={label}
            className="rounded-none bg-transparent"
          />
        </div>
      )}
    </div>
  );
};
